import React, { useEffect, useState } from 'react';

import { Icon } from '@rbilabs/universal-components';
import { useIntl } from 'react-intl';

import { deliveryCompleteTimeout, postOrderTimeout } from 'components/top-service-mode/utils';
import { DeliveryStatus, RbiOrderStatus, useGetUserOrdersQuery } from 'generated/rbi-graphql';
import { useNavigation } from 'hooks/navigation/use-navigation';
import {
  STATUS_MESSAGES,
  STATUS_PERCENTS,
} from 'pages/order-confirmation/delivery/delivery-progress';
import { useAuthContext } from 'state/auth';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useMatch } from 'state/location';
import { useInRestaurantRedemptionContext } from 'state/loyalty/in-restaurant-redemption';
import { OrderStatus, ServiceMode, useOrderContext } from 'state/order';
import { useServiceModeContext } from 'state/service-mode';
import { hiddenAccessibilityPlatformProps } from 'utils/accessibility';
import { routes } from 'utils/routing';

import { Close } from '../top-service-mode-notification.styled';
import {
  IOrderInProgress,
  IOrderInProgressRecentOrder,
  IOrderInProgressResult,
  IServiceNotification,
} from '../types';

const DELIVERY_COMPLETED = [
  DeliveryStatus.ORDER_DROPPED_OFF,
  DeliveryStatus.ORDER_ABANDONED,
  DeliveryStatus.ORDER_CANCELLED,
];

const ORDER_PROGRESS_POLLING_DURATION_IN_MS = 10000;
const DELIVERY_COMPLETED_TIMEOUT_IN_MINUTES = 10;
const PICKUP_UPDATED_TIMEOUT_IN_MINUTES = 10;
const PRE_CURBSIDE_CONFIRM_ARRIVAL_TIMEOUT_IN_MINUTES = 60;

const getDeliveryHeading = ({ deliveryStatus }: { deliveryStatus: DeliveryStatus }) => {
  const deliveryProgress = STATUS_PERCENTS[deliveryStatus];
  if (deliveryProgress <= STATUS_PERCENTS[DeliveryStatus.ORDER_CREATED]) {
    return 'deliveryOrderPlaced';
  }
  if (deliveryProgress < STATUS_PERCENTS[DeliveryStatus.ORDER_PICKED_UP]) {
    return 'deliveryOrderBeingPrepared';
  }
  if (deliveryProgress < STATUS_PERCENTS[DeliveryStatus.ORDER_DROPPED_OFF]) {
    return 'deliveryDriverEnroute';
  }
  return 'deliveryDelivered';
};

const isPollingForDelivery = ({
  hasDelivery,
  isDeliveryOrderCompleted,
  deliveryStatus,
}: {
  hasDelivery: boolean;
  isDeliveryOrderCompleted: boolean;
  deliveryStatus: DeliveryStatus | undefined;
}) =>
  hasDelivery &&
  !isDeliveryOrderCompleted &&
  (!deliveryStatus || !DELIVERY_COMPLETED.includes(deliveryStatus));

export const getInfoForServiceMode = (serviceMode: ServiceMode): IOrderInProgressResult | null => {
  let info: IOrderInProgressResult | null;
  switch (serviceMode) {
    case ServiceMode.DRIVE_THRU:
      info = {
        icon: 'driveThru',
        headingId: 'driveThruOrderPlaced',
        detailsId: 'tapToSeeDetails',
      };
      break;
    case ServiceMode.TAKEOUT:
      info = { icon: 'store', headingId: 'pickUpOrderPlaced', detailsId: 'tapToSeeDetails' };
      break;
    case ServiceMode.EAT_IN:
      info = {
        icon: 'restaurant',
        headingId: 'dineInOrderPlaced',
        detailsId: 'tapToSeeDetails',
      };
      break;
    default:
      info = null;
  }
  return info;
};

const orderInProgress = ({
  recentOrder,
  fireOrderAhead,
  isDeliveryOrderCompleted,
  isPreConfirmCurbside,
  isCurbside,
}: IOrderInProgress): IOrderInProgressResult | null => {
  if (
    recentOrder &&
    [ServiceMode.CATERING_PICKUP, ServiceMode.CATERING_DELIVERY].includes(
      // @ts-expect-error TS(2345) FIXME: Argument of type 'import("/Users/rbish028/src/ctg-... Remove this comment to see the full error message
      recentOrder?.cart.serviceMode
    )
  ) {
    return null;
  }

  const isPickupTimedOut = () => {
    // Add the fire order delay to the total timeout
    const fireOrderInSeconds = recentOrder?.fireOrderIn
      ? recentOrder?.fireOrderIn + fireOrderAhead
      : 0;
    const fireOrderInMinutes = fireOrderInSeconds / 60;
    const timeOutWithDelay = PICKUP_UPDATED_TIMEOUT_IN_MINUTES + fireOrderInMinutes;

    return (
      recentOrder &&
      !recentOrder?.delivery &&
      postOrderTimeout(recentOrder?.updatedAt, timeOutWithDelay)
    );
  };

  // CURBSIDE - Before order is commited on confirming arrival , selecting "I'm here!"
  if (isPreConfirmCurbside) {
    return {
      headingId: 'curbsideOrderSaved',
      detailsId: 'tapWhenYourAreHere',
      icon: (
        <Icon variant="driveThru" color="white" size="6" {...hiddenAccessibilityPlatformProps} />
      ),
    };
  }
  // CURBSIDE - After order is placed
  if (isCurbside && isPickupTimedOut()) {
    return {
      headingId: 'curbsideOrderPlaced',
      detailsId: 'curbsideOrderReadyInstructions',
      icon: (
        <Icon variant="driveThru" color="white" size="6" {...hiddenAccessibilityPlatformProps} />
      ),
    };
  }
  // PICKUP
  const info = getInfoForServiceMode(recentOrder?.cart.serviceMode as ServiceMode);
  if (recentOrder && info && isPickupTimedOut()) {
    return {
      headingId: info?.headingId,
      detailsId: info.detailsId,
      icon: (
        <Icon
          variant={info.icon as any}
          color="white"
          size="6"
          {...hiddenAccessibilityPlatformProps}
        />
      ),
    };
  }
  // DELIVERY
  if (recentOrder?.delivery && !isDeliveryOrderCompleted) {
    const deliveryStatus = recentOrder.delivery.status;
    const headingId = getDeliveryHeading({ deliveryStatus });
    const detailsId = STATUS_MESSAGES[deliveryStatus];
    return {
      headingId,
      detailsId,
      icon: (
        <Icon variant="delivery" color="white" size="6" {...hiddenAccessibilityPlatformProps} />
      ),
    };
  }

  return null;
};

export const useServiceModeNotification = (): IServiceNotification | null => {
  const { formatMessage } = useIntl();
  const fireOrderAhead = useFlag(LaunchDarklyFlag.FIRE_ORDER_AHEAD);
  const { isAuthenticated } = useAuthContext();
  const {
    serviceMode,
    curbsidePickupOrderId,
    curbsidePickupOrderTimePlaced,
    serverOrder,
  } = useOrderContext();
  const { isCurbside } = useServiceModeContext();
  const { linkTo } = useNavigation();

  const {
    inRestaurantRedemptionEnabled,
    inRestaurantRedemptionCart,
  } = useInRestaurantRedemptionContext();

  const [hasSeenCancelModal, setHasSeenCancelModal] = useState(false);
  const [shouldDisplayNotification, setShouldDisplayNotification] = useState<boolean>(true);
  const [shouldDisplayLoyaltyNotification, setShouldDisplayLoyaltyNotification] = useState<boolean>(
    true
  );
  const [currentOrderId, setCurrentOrderId] = useState<string>('');
  const [currentDeliveryStatus, setCurrentDeliveryStatus] = useState<DeliveryStatus>();

  const isOnMenuPage = useMatch({
    path: routes.menu,
    end: false,
  });

  // start polling for recent user orders
  const { data, startPolling, stopPolling, refetch } = useGetUserOrdersQuery({
    variables: {
      limit: 1,
      orderStatuses: [
        RbiOrderStatus.INSERT_SUCCESSFUL,
        RbiOrderStatus.UPDATE_SUCCESSFUL,
        RbiOrderStatus.REFUND_SUCCESSFUL,
      ],
    },
    skip: !isAuthenticated,
  });

  const isPreConfirmCurbside = !!(
    curbsidePickupOrderId &&
    postOrderTimeout(curbsidePickupOrderTimePlaced, PRE_CURBSIDE_CONFIRM_ARRIVAL_TIMEOUT_IN_MINUTES)
  );

  // Upon order confirmation, data from `useGetUseOrdersQuery` is not updated with newly placed order
  // Force a refetch to update query with new confirm order instead of previous order
  const isServerOrderSuccessful: boolean = serverOrder?.status === OrderStatus.INSERT_SUCCESSFUL;
  const isCurbsideOrderCompleted = isCurbside && isServerOrderSuccessful;
  useEffect(() => {
    if (isCurbsideOrderCompleted) {
      refetch();
    }
  }, [isCurbsideOrderCompleted, refetch]);

  useEffect(() => {
    // Update notification for new orders being placed
    const order = data?.userOrders?.orders?.[0];
    if (order && order.rbiOrderId !== currentOrderId) {
      setShouldDisplayNotification(true);
      setCurrentOrderId(order.rbiOrderId);
    }
  }, [currentOrderId, data, isCurbside]);

  useEffect(() => {
    // Update notification for delivery status changes
    const order = data?.userOrders?.orders?.[0];
    if (order?.delivery?.status && order?.delivery.status !== currentDeliveryStatus) {
      setShouldDisplayNotification(true);
      setCurrentDeliveryStatus(order?.delivery?.status);
    }
  }, [currentDeliveryStatus, data]);

  useEffect(() => {
    // reactivate shouldDisplayNotification after closing first Curbside notification
    if (isServerOrderSuccessful) {
      setShouldDisplayNotification(true);
    }
  }, [isServerOrderSuccessful]);

  useEffect(() => {
    // reset shouldDisplayLoyaltyNotification when the loyalty redemption cart is updated.
    if (inRestaurantRedemptionCart?.length > 0) {
      setShouldDisplayLoyaltyNotification(true);
    }
  }, [inRestaurantRedemptionCart]);

  const recentOrder: IOrderInProgressRecentOrder =
    isCurbsideOrderCompleted && serverOrder
      ? { ...serverOrder, updatedAt: serverOrder?.createdAt || '' }
      : data?.userOrders?.orders?.[0];

  if (
    !isOnMenuPage &&
    isAuthenticated &&
    recentOrder?.status !== RbiOrderStatus.REFUND_SUCCESSFUL
  ) {
    const isDeliveryOrderCompleted =
      !!recentOrder?.delivery &&
      DELIVERY_COMPLETED.includes(recentOrder.delivery.status) &&
      (hasSeenCancelModal ||
        deliveryCompleteTimeout(recentOrder?.updatedAt, DELIVERY_COMPLETED_TIMEOUT_IN_MINUTES));

    if (
      isPollingForDelivery({
        hasDelivery: !!recentOrder?.delivery,
        isDeliveryOrderCompleted,
        deliveryStatus: recentOrder?.delivery?.status,
      })
    ) {
      if (hasSeenCancelModal) {
        setHasSeenCancelModal(false);
      }
      startPolling(ORDER_PROGRESS_POLLING_DURATION_IN_MS);
    }

    const inRestaurantRedemptionServiceModes = [
      serviceMode === ServiceMode.TAKEOUT,
      serviceMode === ServiceMode.EAT_IN,
      serviceMode === ServiceMode.DRIVE_THRU,
      serviceMode === ServiceMode.CURBSIDE,
    ];

    if (
      inRestaurantRedemptionServiceModes.includes(true) &&
      inRestaurantRedemptionEnabled &&
      inRestaurantRedemptionCart?.length &&
      shouldDisplayLoyaltyNotification
    ) {
      return {
        heading: formatMessage({ id: 'perksReadyToUse' }),
        details: null,
        icon: <Icon variant="bkCrown" color="#FF8732" />,
        cta: (
          <Close onPress={() => setShouldDisplayLoyaltyNotification(false)}>
            <Icon color="white" variant="cancel" size="6" {...hiddenAccessibilityPlatformProps} />
          </Close>
        ),
      };
    }

    const inProgress = orderInProgress({
      recentOrder,
      fireOrderAhead,
      isPreConfirmCurbside,
      isDeliveryOrderCompleted,
      isCurbside,
    });

    if (inProgress && shouldDisplayNotification) {
      const { headingId, detailsId, icon } = inProgress;
      const orderId = isPreConfirmCurbside ? curbsidePickupOrderId : currentOrderId;
      return {
        heading: formatMessage({ id: headingId }),
        details: formatMessage({ id: detailsId }),
        icon,
        cta: (
          <Close onPress={() => linkTo(`${routes.orderConfirmation}/${orderId}`)}>
            <Icon color="white" variant="forward" size="6" {...hiddenAccessibilityPlatformProps} />
          </Close>
        ),
        orderId,
      };
    }
  }

  // Stop polling for recent user orders
  stopPolling();

  return null;
};
